import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const { user } = useAuth();
    const [cart, setCart] = useState({ items: [], subtotal: 0 });
    const [loading, setLoading] = useState(false);
    const [loadingProductId, setLoadingProductId] = useState(null);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem('cart_session_id'));
    const [prevUserId, setPrevUserId] = useState(user?.user_id);

    const fetchCart = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const currentSessionId = localStorage.getItem('cart_session_id');
            const params = currentSessionId ? `?session_id=${currentSessionId}` : '';
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const response = await fetch(`${API_URL}/cart${params}`, {
                credentials: 'include',
                headers,
            });
            
            if (response.ok) {
                const data = await response.json();
                setCart(data);
                // Update sessionId if returned from server
                if (data.session_id) {
                    setSessionId(data.session_id);
                    localStorage.setItem('cart_session_id', data.session_id);
                }
            } else {
                // If fetch fails (e.g., unauthorized), clear cart state
                setCart({ items: [], subtotal: 0 });
            }
        } catch (error) {
            console.error('Fetch cart error:', error);
            setCart({ items: [], subtotal: 0 });
        } finally {
            setLoading(false);
        }
    }, []);

    // Handle user state changes (login/logout)
    useEffect(() => {
        const currentUserId = user?.user_id;
        
        // User logged out
        if (prevUserId && !currentUserId) {
            // Clear cart state immediately on logout
            setCart({ items: [], subtotal: 0 });
            // Clear session ID state (localStorage already cleared by AuthContext)
            setSessionId(null);
        }
        // User logged in or changed
        else if (currentUserId !== prevUserId) {
            // Fetch user's cart
            fetchCart();
        }
        
        setPrevUserId(currentUserId);
    }, [user?.user_id, prevUserId, fetchCart]);

    // Initial fetch on mount
    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    const addToCart = async (productId, quantity = 1) => {
        try {
            setLoadingProductId(productId);
            const currentSessionId = localStorage.getItem('cart_session_id');
            const params = currentSessionId ? `?session_id=${currentSessionId}` : '';
            const token = localStorage.getItem('auth_token');
            const headers = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const response = await fetch(`${API_URL}/cart/add${params}`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: JSON.stringify({ product_id: productId, quantity }),
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.session_id) {
                    setSessionId(data.session_id);
                    localStorage.setItem('cart_session_id', data.session_id);
                }
                await fetchCart();
                return { success: true };
            }
            const err = await response.json().catch(() => ({}));
            return { success: false, detail: err.detail || '' };
        } catch (error) {
            console.error('Add to cart error:', error);
            return { success: false, detail: '' };
        } finally {
            setLoadingProductId(null);
        }
    };

    const updateQuantity = async (productId, quantity) => {
        try {
            setLoading(true);
            const currentSessionId = localStorage.getItem('cart_session_id');
            const params = currentSessionId ? `?session_id=${currentSessionId}` : '';
            const token = localStorage.getItem('auth_token');
            const headers = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const response = await fetch(`${API_URL}/cart/update${params}`, {
                method: 'PUT',
                headers,
                credentials: 'include',
                body: JSON.stringify({ product_id: productId, quantity }),
            });
            
            if (response.ok) {
                await fetchCart();
                return true;
            }
            return false;
        } catch (error) {
            console.error('Update cart error:', error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const removeFromCart = async (productId) => {
        try {
            setLoading(true);
            const currentSessionId = localStorage.getItem('cart_session_id');
            const params = currentSessionId ? `?session_id=${currentSessionId}` : '';
            const token = localStorage.getItem('auth_token');
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const response = await fetch(`${API_URL}/cart/remove/${productId}${params}`, {
                method: 'DELETE',
                credentials: 'include',
                headers,
            });
            
            if (response.ok) {
                await fetchCart();
                return true;
            }
            return false;
        } catch (error) {
            console.error('Remove from cart error:', error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const clearCart = () => {
        setCart({ items: [], subtotal: 0 });
    };

    const itemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

    const value = {
        cart,
        loading,
        loadingProductId,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        fetchCart,
        itemCount,
        sessionId,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export default CartContext;
